import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const page = parseInt(params.get("page") || "1", 10);
  const limit = 12;
  const category_id = params.get("category_id") || "";
  const search = params.get("search") || "";
  const featured = params.get("featured") || "";
  const min_price = params.get("min_price") || "";
  const max_price = params.get("max_price") || "";

  const [priceRange, setPriceRange] = useState({ min: min_price, max: max_price });

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = { page, limit };
    if (category_id) p.category_id = category_id;
    if (search) p.search = search;
    if (featured) p.featured = featured;
    if (min_price) p.min_price = min_price;
    if (max_price) p.max_price = max_price;
    api
      .get("/products", { params: p })
      .then((r) => {
        setProducts(r.data.items);
        setTotal(r.data.total);
      })
      .finally(() => setLoading(false));
  }, [page, category_id, search, featured, min_price, max_price]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const updateParam = (key, value) => {
    const np = new URLSearchParams(params);
    if (value === "" || value === null || value === undefined) np.delete(key);
    else np.set(key, value);
    np.delete("page");
    setParams(np);
  };

  const applyPrice = () => {
    const np = new URLSearchParams(params);
    priceRange.min ? np.set("min_price", priceRange.min) : np.delete("min_price");
    priceRange.max ? np.set("max_price", priceRange.max) : np.delete("max_price");
    np.delete("page");
    setParams(np);
  };

  const resetFilters = () => {
    setParams(new URLSearchParams());
    setPriceRange({ min: "", max: "" });
  };

  const heading = useMemo(() => {
    if (featured) return "Featured";
    if (category_id) {
      const c = categories.find((x) => x.id === category_id);
      return c ? c.name : "Shop";
    }
    if (search) return `Results for "${search}"`;
    return "All products";
  }, [featured, category_id, search, categories]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="shop-page">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="text-xs uppercase tracking-ultra text-neutral-500">Shop</div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tighter leading-none mt-2">
            {heading}
          </h1>
        </div>
        <div className="text-xs text-neutral-500">{total} items</div>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-10">
        {/* Filters */}
        <aside className="space-y-8 text-sm" data-testid="shop-filters">
          <div>
            <div className="text-[10px] uppercase tracking-ultra text-neutral-500 mb-3">
              Category
            </div>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => updateParam("category_id", "")}
                  className={`hover:text-accent ${!category_id ? "font-bold" : ""}`}
                  data-testid="filter-cat-all"
                >
                  All
                </button>
              </li>
              {categories.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => updateParam("category_id", c.id)}
                    className={`hover:text-accent ${category_id === c.id ? "font-bold" : ""}`}
                    data-testid={`filter-cat-${c.slug}`}
                  >
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-ultra text-neutral-500 mb-3">
              Price
            </div>
            <div className="flex gap-2">
              <input
                type="number"
                placeholder="Min"
                value={priceRange.min}
                onChange={(e) => setPriceRange({ ...priceRange, min: e.target.value })}
                className="w-full border-b border-black bg-transparent py-2 text-sm outline-none"
                data-testid="filter-min-price"
              />
              <input
                type="number"
                placeholder="Max"
                value={priceRange.max}
                onChange={(e) => setPriceRange({ ...priceRange, max: e.target.value })}
                className="w-full border-b border-black bg-transparent py-2 text-sm outline-none"
                data-testid="filter-max-price"
              />
            </div>
            <button
              onClick={applyPrice}
              className="mt-3 text-[10px] uppercase tracking-ultra border border-black px-3 py-2 hover:bg-black hover:text-white transition-colors"
              data-testid="filter-apply-price"
            >
              Apply
            </button>
          </div>

          <button
            onClick={resetFilters}
            className="text-[10px] uppercase tracking-ultra underline underline-offset-4 text-neutral-600 hover:text-black"
            data-testid="filter-reset"
          >
            Reset filters
          </button>
        </aside>

        {/* Products */}
        <div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
              {Array(6)
                .fill(0)
                .map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-[4/5] bg-neutral-200" />
                    <div className="h-3 w-3/4 bg-neutral-200 mt-4" />
                    <div className="h-3 w-1/3 bg-neutral-200 mt-2" />
                  </div>
                ))}
            </div>
          ) : products.length === 0 ? (
            <div className="py-24 text-center border border-black/10" data-testid="shop-empty">
              <div className="text-xs uppercase tracking-ultra text-neutral-500">
                No products found
              </div>
              <h3 className="text-3xl font-black tracking-tighter mt-4">
                Try different filters
              </h3>
              <button
                onClick={resetFilters}
                className="mt-6 bg-black text-white px-6 py-3 text-xs uppercase tracking-ultra"
                data-testid="shop-empty-reset"
              >
                Reset filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-12" data-testid="shop-pagination">
              {Array.from({ length: totalPages }).map((_, i) => {
                const n = i + 1;
                return (
                  <button
                    key={n}
                    onClick={() => {
                      const np = new URLSearchParams(params);
                      np.set("page", String(n));
                      setParams(np);
                    }}
                    className={`w-10 h-10 text-xs uppercase tracking-ultra border ${
                      page === n
                        ? "bg-black text-white border-black"
                        : "border-black hover:bg-black hover:text-white"
                    }`}
                    data-testid={`shop-page-${n}`}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
